import { ref } from "vue";
import type AlgoliaData from "~~/types/AlgoliaData";
import { debounce } from "perfect-debounce";
import type PodcastEpisode from "~~/types/PodcastEpisode";
import type PodcastType from "~~/types/Podcast";
import type StatamicCollectionRequest from "~~/types/requests/StatamicCollectionRequest";

export function usePodcast() {
    const route = useRoute();
    const router = useRouter();
    const config = useRuntimeConfig();
    const loading = ref(true);
    const page = ref(1);
    const limit = computed(() => {
        // Get limit from query
        if (route.query) {
            for (const [key, value] of Object.entries(route.query)) {
                if (key === "limit") {
                    return value as string;
                }
            }
        }
        return "5";
    });
    const result = ref<AlgoliaData | any>();
    const searchText = ref(route.query.searchEpisode ? String(route.query.searchEpisode) : "");
    const updateUrl = ref(true);
    // Set page from slug
    const params = route.params as {};
    if (params && Object.keys(params).length > 0) {
        for (const [key, value] of Object.entries(params)) {
            if (key.toLowerCase() == "page") {
                page.value = value as number;
            }
        }
    }

    const podcastInfo = computed(() => {
        return (result.value?.podcast as PodcastType) || [];
    });

    // Computed property to filter episodes based on search text and limit
    const episodes = computed(() => {
        return (result.value?.episodes as PodcastEpisode[]) || [];
    });

    async function fetchResult() {
        loading.value = true;

        // It doesn't look like the RSS feed reader support search/limit
        // So we will fetch all episodes and filter them in the frontend
        const { data } = await useFetch<StatamicCollectionRequest>(config.public.STATAMIC_API + `/podcast`, {
            deep: true,
        })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                loading.value = false;
            });

        result.value = data.value ?? [];

        return result.value;
    }

    const debouncePerformFilter = debounce(async () => {
        if (updateUrl.value) {
            // Replace page in path
            const path = router.currentRoute.value.path.replace(/\/page\/\d+/, "/page/" + page.value);
            // update the url with the search value
            // this doesn't actually cause the page to reload
            if (searchText.value) {
                router.replace({ path, query: { searchEpisode: searchText.value } });
            } else {
                router.replace({ path, query: {} });
            }
        }
    }, 1000);

    watch(searchText, () => {
        page.value = 1; // Reset page to 1
        debouncePerformFilter();
    });
    // Watch router query changes
    watch(
        () => route.query,
        async () => {
            // Set searchText from query to trigger search if searchText is different
            searchText.value = route.query.searchEpisode ? String(route.query.searchEpisode) : "";
        },
        { immediate: true },
    );

    return {
        loading,
        page,
        searchText,
        updateUrl,
        result,
        episodes,
        podcastInfo,
        fetchResult,
    };
}
